import { ButtonBase, ButtonBaseProps } from '@mui/material';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import { SvgIconTypeMap } from '@mui/material/SvgIcon';
import { css, styled as muiStyled } from '@mui/material/styles';
import React from 'react';

import {
  LEFT_NAV_ITEM_HEIGHT,
  LEFT_NAV_ICON_SIZE,
  LEFT_NAV_FONT_SIZE,
} from './_type';
import { useLeftNavSubmenuContext } from './left-nav-submenu-context';

import {
  DesignToken,
  type DesignTokenSet,
} from '~/client/components/base/core';
import { spc } from '~/client/components/base/spacer';
import Text from '~/client/components/base/text';
import { useThemeMode } from '~/client/contexts/theme-context';
import { useDesignTokens } from '~/client/hooks/use-design-tokens';

interface Props extends Omit<ButtonBaseProps, 'component'> {
  className?: string;
  /**
   * Icon to render on the left side of text
   */
  icon?: OverridableComponent<SvgIconTypeMap>;
  /**
   * Selected component has a colored background
   */
  selected?: boolean;
  /**
   * Custom component to render as (e.g., Link for navigation)
   * When provided, ButtonBase renders as this component instead of button
   */
  component?: React.ElementType;
  /**
   * Route destination for Link components
   */
  to?: string;
  /**
   * URL for anchor elements
   */
  href?: string;
}

const LeftNavFlyoutMenuItemImpl = React.forwardRef<HTMLElement, Props>(
  ({ className, icon, children, component, to, href, ...props }, ref) => {
    const Icon = icon;

    // Build props for ButtonBase, conditionally including component/to/href
    // This allows ButtonBase to render as a Link when component is provided
    const buttonBaseProps = {
      ref: ref as React.Ref<HTMLButtonElement>,
      className,
      role: 'menuitem' as const,
      ...(component ? { component } : {}),
      ...(to ? { to } : {}),
      ...(href ? { href } : {}),
      ...props,
    };

    return (
      <ButtonBase {...(buttonBaseProps as any)}>
        {Icon && (
          <Icon className="icon" sx={{ fontSize: LEFT_NAV_ICON_SIZE }} />
        )}
        <div className="menu-item-content">
          {typeof children === 'string' ? (
            <Text sz="body14" noWrap align="left" color="inherit">
              {children}
            </Text>
          ) : (
            children
          )}
        </div>
      </ButtonBase>
    );
  }
);

LeftNavFlyoutMenuItemImpl.displayName = 'LeftNavFlyoutMenuItem';

const LeftNavFlyoutMenuItemStyled = muiStyled(LeftNavFlyoutMenuItemImpl, {
  shouldForwardProp: (prop) =>
    prop !== 'tokens' && prop !== 'isDark' && prop !== 'isInline',
})<Props & { tokens?: DesignTokenSet; isDark?: boolean; isInline?: boolean }>(({
  theme,
  selected,
  tokens,
  isDark,
  isInline,
}) => {
  const t = tokens ?? DesignToken;
  const nav = t.nav;
  // When inline (expanded sidebar), use white overlay so selection is visible on the teal sidebar
  const tealBg = `var(--org-primary-color, ${t.brand.primary})`;
  const selectedBg = isInline
    ? nav.activeBgDark
    : isDark
      ? nav.activeBgDark
      : t.surface2;
  const hoverBg = isInline
    ? 'rgba(255,255,255,0.06)'
    : isDark
      ? nav.activeBgDark
      : t.surface2;
  const textColor = isInline
    ? selected
      ? nav.itemTextOnDarkHover
      : nav.itemTextOnDark
    : selected
      ? isDark
        ? nav.itemTextOnDarkHover
        : t.accent
      : isDark
        ? t.nav.itemTextOnDark
        : t.textPrimary;
  const hoverColor = isInline
    ? nav.itemTextOnDarkHover
    : isDark
      ? nav.itemTextOnDarkHover
      : t.accent;
  const barColor = `var(--org-primary-color, ${nav.activeIndicator})`;
  const { disabledOpacity, activatedOpacity } = theme.palette.action;

  return css`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    background-color: ${selected ? selectedBg : 'transparent'};
    border-radius: ${t.radiusSmall}px;
    margin: 0 ${theme.spacing(spc.xs)};
    width: calc(100% - ${theme.spacing(spc.xs * 2)});
    padding: ${theme.spacing(spc.xs)} ${theme.spacing(spc.s)};
    height: ${LEFT_NAV_ITEM_HEIGHT}px;
    position: relative;

    color: ${selected
      ? isInline
        ? `var(--org-primary-contrast, ${textColor})`
        : isDark
          ? `var(--org-primary-contrast, ${textColor})`
          : textColor
      : textColor};

    transition:
      background-color 0.2s ease,
      color 0.2s ease;

    ${selected && isInline
      ? css`
          &::before {
            content: '';
            position: absolute;
            left: 0;
            top: 0;
            bottom: 0;
            width: 3px;
            background-color: ${barColor};
            border-radius: 0;
          }
        `
      : ''}

    .MuiTypography-root {
      color: inherit;
      font-weight: ${selected ? 500 : 400};
      font-size: ${LEFT_NAV_FONT_SIZE}px;
      line-height: 1.4;
    }

    .icon {
      font-size: ${LEFT_NAV_ICON_SIZE}px !important;
      width: ${LEFT_NAV_ICON_SIZE}px;
      min-width: ${LEFT_NAV_ICON_SIZE}px;
      flex-shrink: 0;
      margin-right: ${theme.spacing(spc.xs)};
      color: inherit;
    }

    :disabled {
      opacity: ${disabledOpacity};
    }

    :hover {
      background-color: ${selected ? selectedBg : hoverBg};
      color: ${hoverColor};

      .MuiTypography-root {
        color: inherit;
      }

      .icon {
        color: inherit;
      }
    }

    :active {
      opacity: ${activatedOpacity};
    }

    .menu-item-content {
      flex: 1 1 auto;
      min-width: 0;
      overflow: hidden;
      text-align: left;
    }
  `;
});

/**
 * Menu item for use inside LeftNavFlyoutMenu
 *
 * Can be used as a Link by passing component={Link} and to="..."
 * This avoids invalid HTML nesting of button inside anchor
 */
export const LeftNavFlyoutMenuItem = React.forwardRef<HTMLElement, Props>(
  (props, ref) => {
    const t = useDesignTokens();
    const { effectiveMode } = useThemeMode();
    const isDark = effectiveMode === 'dark';
    const { isInline } = useLeftNavSubmenuContext();
    return (
      <LeftNavFlyoutMenuItemStyled
        ref={ref as React.Ref<HTMLButtonElement>}
        tokens={t}
        isDark={isDark}
        isInline={isInline}
        {...props}
      />
    );
  }
);
LeftNavFlyoutMenuItem.displayName = 'LeftNavFlyoutMenuItem';
